import React, { useMemo, useState } from 'react';
import { useQuoteActions } from 'hooks/useQuoteActions';
import { useConfigPersistence } from 'hooks/useConfigPersistence';
import { useSpecialPricingInputs } from 'hooks/useSpecialPricingInputs';
import { useQuoteValidation, validateQuoteField } from 'hooks/useQuoteValidation';
import { useAccountExecutives, DEV_BUILD_SHOWS_TEST_EXECS } from 'hooks/useAccountExecutives';
import { useExecConfirmDialog } from 'components/ExecConfirmDialog';
import {
  useConfigInputs, useConfigActions, useMetrics, useBaselineMetrics, useBom, usePricing, useVendorCtx, useExecCtx, useToastCtx,
} from 'shared/state/configContexts';
import { QuoteActionsCtx, type QuoteActionsValue } from 'shared/state/quoteActionsContext';
import { usePermissions } from './PermissionsProvider';
import type { AppUser } from 'shared/state/types';
import { useRepriceActions } from './useRepriceActions';

/** The one place the Phase 0b quote hooks are called in the V2 tree. It assembles
 *  their grouped dependency objects from the Phase 1 contexts, and publishes the
 *  result on one context; sections consume it through useQuoteActionsCtx.
 *
 *  Ruling P7: useSpecialPricingInputs' `pricing`/`viewState` slices come from
 *  useConfigInputs() — NOT `config` — because the hook reads
 *  pricing.baseSsdSoftware / pricing.baseHddSoftware / pricing.discountMonths /
 *  pricing.subscriptionMonths / pricing.ssdSupportUnitPrice and viewState.isNoReg,
 *  none of which live on the flat config object. */
export function QuoteActionsProvider({ user, children }: { user: AppUser; children: React.ReactNode }) {
  const { config, system, pricing, viewState, metadata } = useConfigInputs();
  const { updateConfig, batchUpdateConfig, loadConfig } = useConfigActions();
  // CONF-212: the real metrics feed pricing/BOM/CCI; displayMetrics (overrides
  // applied) is what the customer artifacts print — useQuoteActions splits them.
  const metrics = useBaselineMetrics();
  const displayMetrics = useMetrics();
  const bom = useBom();
  const { pricingData, priceBook, prices, priceEditions } = usePricing();
  const { vendor, productDb, switchVendor } = useVendorCtx();
  const exec = useExecCtx();
  const { showToast } = useToastCtx();
  const { hasSpecialAccess, hasExecAccess, hasAdminAccess } = usePermissions();
  const { dialogElement: execConfirmElement, confirmExecEscape } = useExecConfirmDialog();
  const { executives: accountExecutives } = useAccountExecutives({ includeTest: hasAdminAccess || DEV_BUILD_SHOWS_TEST_EXECS });

  const [showExportDialog, setShowExportDialog] = useState(false);
  const [activeView, setActiveView] = useState<'BaseSystem' | 'Expansion'>('BaseSystem');

  const userName = user.email ?? (user.attributes?.email as string | undefined) ?? user.username ?? '';
  const { quoteErrors, setQuoteErrors, setFieldError } = useQuoteValidation();

  const sp = useSpecialPricingInputs({
    data: { pricing, viewState, config, vendor },
    config: { updateConfig },
  });

  const persistence = useConfigPersistence({
    // productDb: priceBook — matching classic (App.js:561). The repo's one
    // pricing seam is that every pricing consumer takes the priceBook (the
    // live edition merged over productDb); raw productDb here would seed a
    // fresh config's software prices from spec-sheet data.
    data: { vendor, productDb: priceBook },
    access: { hasSpecialAccess, hasExecAccess },
    exec: {
      execPricing: exec.execPricing, pinSavedPrices: exec.pinSavedPrices, releasePin: exec.releasePin,
      setCustomPrices: exec.setCustomPrices, loadedQuoteIdRef: exec.loadedQuoteIdRef, execPricingTouched: exec.execPricingTouched,
      execMetrics: exec.execMetrics,
    },
    config: { loadConfig, switchVendor, setActiveView, updateConfig },
    ui: { showToast, onLoaded: sp.applyLoadedSoftwarePrices },
  });

  const actions = useQuoteActions({
    data: { config, system, metadata, metrics, displayMetrics, bom, pricingData, priceBook, productDb, prices, vendor },
    access: { hasSpecialAccess, user, userName },
    exec: { execPricing: exec.execPricing, execMetrics: exec.execMetrics, confirmExecEscape },
    config: { updateConfig, batchUpdateConfig },
    ui: { showToast, setQuoteErrors, setShowExportDialog, setShowSaveDialog: persistence.setShowSaveDialog, validateQuoteField },
  });

  const { startReprice, acceptReprice, repriceDiff, setRepriceDiff, qtyByPartNumber } = useRepriceActions({
    exec, config, priceBook, bom, user, vendor, priceEditions, showToast,
  });

  const value = useMemo<QuoteActionsValue>(() => ({
    ...actions,
    // Cast: useQuoteActions is untyped JS whose logEscape default parameters
    // (`quoteSerial = null, quoteName = null`) narrow TS's inferred opts type
    // to `null` rather than the broader `string | null` this context
    // publishes. Narrowed to just this one field, not the whole return —
    // `...actions` above still carries every other field at its real,
    // checked type.
    logEscape: actions.logEscape as QuoteActionsValue['logEscape'],
    // Cast: useConfigPersistence is untyped JS whose `quoteRow = null` default
    // parameter narrows TS's inferred type to `null` rather than the broader
    // `Record<string, unknown> | null` this context publishes.
    loadSavedConfig: persistence.loadSavedConfig as QuoteActionsValue['loadSavedConfig'],
    resetConfig: persistence.resetConfig,
    userName,
    showSaveDialog: persistence.showSaveDialog, setShowSaveDialog: persistence.setShowSaveDialog,
    showQuotesModal: persistence.showQuotesModal, setShowQuotesModal: persistence.setShowQuotesModal,
    showExportDialog, setShowExportDialog,
    quoteErrors, setFieldError,
    sp, accountExecutives,
    startReprice, acceptReprice, repriceDiff, setRepriceDiff, qtyByPartNumber,
    execConfirmElement,
  }), [actions, persistence, showExportDialog, quoteErrors, setFieldError, sp, accountExecutives,
    startReprice, acceptReprice, repriceDiff, setRepriceDiff, qtyByPartNumber, execConfirmElement, userName]);

  // activeView is held so useConfigPersistence keeps its classic contract; the V2
  // shell reads systemType from config, so nothing renders from it.
  void activeView;

  return <QuoteActionsCtx.Provider value={value}>{children}</QuoteActionsCtx.Provider>;
}
